import type { FlowDataPoint } from '../hooks/useMeterFlow';

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

/**
 * Epoch ms for a flow point's naive meter-local `datetime`, or null when it
 * has none or it doesn't parse. The type guard matters: `new Date(null)` is
 * epoch 0, a valid date.
 */
export function parseFlowTimestamp(datetime: unknown): number | null {
  if (typeof datetime !== 'string') return null;
  const ts = new Date(datetime).getTime();
  return Number.isFinite(ts) ? ts : null;
}

/**
 * Compute the x-axis domain for the meter inspector consumption charts
 * (`MeterFlowChart` and `MeterAlertConsumptionChart`).
 *
 * - Spans every point of every series passed (Plus `flow`, plus any overlay
 *   series such as Classic flow). Points whose `datetime` doesn't parse are
 *   ignored, so one bad point in an overlay can't break the whole axis.
 * - Left edge: earliest first data point minus a 2-hour pad (so edge markers
 *   don't clip at the y-axis).
 * - Right edge: the later of the latest data point and the picker's `to`,
 *   capped at `Date.now()` so future dates never render.
 */
export function computeConsumptionXDomain(
  flow: FlowDataPoint[] | undefined,
  dateRangeTo: Date,
  overlay?: FlowDataPoint[],
): [number, number] | undefined {
  let first = Infinity;
  let last = -Infinity;
  for (const series of [flow, overlay]) {
    for (const point of series ?? []) {
      const ts = parseFlowTimestamp(point.datetime);
      if (ts == null) continue;
      if (ts < first) first = ts;
      if (ts > last) last = ts;
    }
  }
  if (!Number.isFinite(first)) return undefined;
  const rightEdge = Math.min(Date.now(), Math.max(last, dateRangeTo.getTime()));
  return [first - TWO_HOURS_MS, rightEdge];
}

/**
 * One tick per whole day in the domain (skipping the partial first day).
 * Ticks are meter-time since flow timestamps are meter-local.
 */
export function computeDailyXTicks(xDomain: [number, number] | undefined): number[] {
  if (!xDomain) return [];
  const ticks: number[] = [];
  const start = new Date(xDomain[0]);
  const day = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  day.setDate(day.getDate() + 1); // skip partial first day
  while (day.getTime() <= xDomain[1]) {
    ticks.push(day.getTime());
    day.setDate(day.getDate() + 1);
  }
  return ticks;
}
